import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { percentToTimestamp, useTimelineDrag } from '../../../hooks/useTimelineDrag';
import { alignDurationToKeyframe, alignToKeyframe, CutTimelineEvent, Timeline } from '../../../parser';

/** Minimum length of a video trim cut, in seconds. */
const MIN_CUT_DURATION_SECONDS = 10;

export const VideoTrim = ({ duration, timeline, onClick }: { timeline: Timeline; duration: Duration; onClick: (event: CutTimelineEvent) => void }) => {
    const { t } = useTranslation();
    const [cuts, setCuts] = useState<CutTimelineEvent[]>([]);

    useEffect(() => {
        if (!timeline?.cuts || duration.toMillis() === 0) {
            setCuts([]);
            return;
        }

        setCuts(timeline.cuts);
    }, [timeline?.cuts, duration]);

    const sortedCuts = () => [...timeline.cuts.sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis())];

    const markChanged = (cut: CutTimelineEvent) => {
        cut.changed = true;
        timeline.changed = true;
        timeline.cuts = [...timeline.cuts];
    };

    const drag = useTimelineDrag<CutTimelineEvent>((cut, position) => {
        // Video time works in seconds, so we need to round the timestamp to the nearest second
        cut.timestamp = alignToKeyframe(timeline, percentToTimestamp(duration, position));
        cut.duration = alignDurationToKeyframe(timeline, cut.timestamp, cut.desiredDuration);
        setCuts(sortedCuts());
    }, markChanged);

    const resize = useTimelineDrag<CutTimelineEvent>((cut, position) => {
        const timelinePosition = percentToTimestamp(duration, position);
        let newDuration = timelinePosition.minus(cut.timestamp).as('seconds');
        if (newDuration < MIN_CUT_DURATION_SECONDS) {
            newDuration = MIN_CUT_DURATION_SECONDS;
        }
        cut.desiredDuration = Duration.fromObject({ seconds: newDuration });
        cut.duration = alignDurationToKeyframe(timeline, cut.timestamp, cut.desiredDuration);
        setCuts(sortedCuts());
    }, markChanged);

    const onDragStart = (e: React.MouseEvent<HTMLButtonElement>, cut: CutTimelineEvent) =>
        drag.begin(e, cut, (cut.timestamp.toMillis() / duration.toMillis()) * 100);

    const onResizeStart = (e: React.MouseEvent<HTMLButtonElement>, cut: CutTimelineEvent) =>
        resize.begin(e, cut, (cut.timestamp.plus(cut.duration).toMillis() / duration.toMillis()) * 100);

    return (
        <>
            {cuts.map((cut, index) => {
                const left = (cut.timestamp.toMillis() / duration.toMillis()) * 100;
                const width = (cut.duration.toMillis() / duration.toMillis()) * 100;
                return (
                    <div
                        key={index}
                        style={{
                            left: `${left}%`,
                            width: `${width}%`
                        }}
                        className={`col-start-1 row-span-full track video-trim ${cut.changed ? 'changed' : ''}`}
                    >
                        <button onMouseDown={(event) => onDragStart(event, cut)}></button>
                        <span onClick={() => onClick(cut)}>
                            <span>{t('Editor.TrimVideo.TrimNumber', { number: index + 1 })}</span>
                        </span>
                        <button onMouseDown={(event) => onResizeStart(event, cut)}></button>
                    </div>
                );
            })}
        </>
    );
};
